import moment from "moment";

import { formatDate } from "helpers/Helpers";
import { IProduct, IVariant } from "redux/reducers/models/product";

import { Day, DayValue } from "./TeeSheetPricingSheets";

export type PricingSheetApplicationValue =
  | "green_fee"
  | "power_cart"
  | "no_show"
  | "green_fee_league"
  | "power_cart_league";

export type PricingSheetApplicationOption = {
  value: PricingSheetApplicationValue;
  label: string;
};

export const PRICING_SHEET_APPLICATIONS: PricingSheetApplicationOption[] = [
  { value: "green_fee", label: "Green Fee" },
  { value: "power_cart", label: "Power Cart" },
  { value: "no_show", label: "No Show" },
  { value: "green_fee_league", label: "League Green Fee" },
  { value: "power_cart_league", label: "League Power Cart" },
];

export const DEFAULT_PRICING_SHEET_APPLICATION: PricingSheetApplicationValue = "green_fee";

export const PRICING_SHEET_PRODUCT_TYPES = [
  "Green Fee",
  "Power Cart",
  "No Show Green Fee",
  "Combo",
] as const;

export const PRICING_SHEET_LIST_PATH = "/admin/settings/tee-sheet/pricing-sheet";
export const CHECK_IN_CONFIG_PATH = "/admin/settings/tee-sheet/check-in-config";

export type PricingSheetSavedLocationState = {
  showPricingSheetSavedCallout?: boolean;
  application?: PricingSheetApplicationValue;
  dayOfWeek?: string;
};

export const SESSION_FILTER_DAY_KEY = "retainedSelectedDay";
export const SESSION_FILTER_APPLICATION_KEY = "retainedPricingSheetApplication";
/** Facility list only. Kept separate so "All" does not overwrite the concrete day used by new sheets. */
export const SESSION_LIST_DAY_FILTER_KEY = "retainedPricingSheetListDayFilter";
/** Facility list only. Kept separate so "All" does not overwrite the rate type used by new sheets. */
export const SESSION_LIST_APPLICATION_FILTER_KEY = "retainedPricingSheetListApplication";
export const ALL_PRICING_SHEET_DAYS = "all";
export const ALL_PRICING_SHEET_APPLICATIONS = "all";

export type PricingSheetApplicationFilter =
  | PricingSheetApplicationValue
  | typeof ALL_PRICING_SHEET_APPLICATIONS;

export type PricingSheetDayFilter = DayValue | typeof ALL_PRICING_SHEET_DAYS;

const CONCRETE_DAY_VALUES = new Set<number>([
  Day.Monday,
  Day.Tuesday,
  Day.Wednesday,
  Day.Thursday,
  Day.Friday,
  Day.Saturday,
  Day.Sunday,
  Day.Holiday,
]);

export type TProductVariantGroup = {
  productId: number;
  productTitle: string;
  isMultiVariant: boolean;
  variants: Array<Partial<IVariant>>;
};

export interface IPricingSheetFormState {
  id?: number;
  title: string;
  application: PricingSheetApplicationValue;
  day_of_week: string;
  start_time: string;
  end_time: string;
  course_id: number | null;
  startDate: Date;
  endDate: Date;
  date_range: string[];
}

export interface IProductPickerState {
  addProductVisibility: boolean;
  selectedProducts: IProduct[];
  addedVariants: Array<Partial<IVariant>>;
  productSearchResults: IProduct[] | undefined;
  productSearchTotalCount: number | undefined;
  productSearchPending: boolean;
  productSearchLoadingMore: boolean;
  productSearchSelectAllPending: boolean;
}

export function getStoredDay(): DayValue {
  const stored =
    window.sessionStorage.getItem(SESSION_FILTER_DAY_KEY) ??
    window.sessionStorage.getItem("retainedDayState");
  if (stored === null || stored === undefined) {
    return Day.Monday;
  }

  const numeric = Number(stored);
  if (!CONCRETE_DAY_VALUES.has(numeric)) {
    return Day.Monday;
  }

  return numeric as DayValue;
}

export function getStoredListDayFilter(): PricingSheetDayFilter {
  const stored = window.sessionStorage.getItem(SESSION_LIST_DAY_FILTER_KEY);
  if (stored === ALL_PRICING_SHEET_DAYS) {
    return ALL_PRICING_SHEET_DAYS;
  }

  if (stored !== null) {
    const numeric = Number(stored);
    if (CONCRETE_DAY_VALUES.has(numeric)) {
      return numeric as DayValue;
    }
  }

  return getStoredDay();
}

export function persistListDayFilter(filter: PricingSheetDayFilter) {
  window.sessionStorage.setItem(SESSION_LIST_DAY_FILTER_KEY, String(filter));
  if (filter !== ALL_PRICING_SHEET_DAYS) {
    window.sessionStorage.setItem(SESSION_FILTER_DAY_KEY, String(filter));
  }
}

export function getStoredDayLabel(): string {
  return Day[getStoredDay()];
}

export function getStoredApplication(): PricingSheetApplicationValue {
  const stored = window.sessionStorage.getItem(SESSION_FILTER_APPLICATION_KEY);
  const match = PRICING_SHEET_APPLICATIONS.find(app => app.value === stored);
  return match?.value ?? DEFAULT_PRICING_SHEET_APPLICATION;
}

export function getPricingSheetApplicationLabel(value?: string): string {
  return PRICING_SHEET_APPLICATIONS.find(app => app.value === value)?.label ?? value ?? "";
}

export function getStoredListApplication(): PricingSheetApplicationFilter {
  const stored = window.sessionStorage.getItem(SESSION_LIST_APPLICATION_FILTER_KEY);
  if (stored === ALL_PRICING_SHEET_APPLICATIONS) {
    return ALL_PRICING_SHEET_APPLICATIONS;
  }

  const match = PRICING_SHEET_APPLICATIONS.find(app => app.value === stored);
  return match?.value ?? getStoredApplication();
}

export function persistListApplication(filter: PricingSheetApplicationFilter) {
  window.sessionStorage.setItem(SESSION_LIST_APPLICATION_FILTER_KEY, filter);
  if (filter !== ALL_PRICING_SHEET_APPLICATIONS) {
    window.sessionStorage.setItem(SESSION_FILTER_APPLICATION_KEY, filter);
  }
}

export function getDayValueFromLabel(dayLabel: string): DayValue {
  const dayValue = Day[dayLabel as keyof typeof Day];
  return typeof dayValue === "number" ? (dayValue as DayValue) : Day.Monday;
}

export function persistPricingSheetFilters(application: PricingSheetApplicationValue, dayLabel: string) {
  window.sessionStorage.setItem(SESSION_FILTER_APPLICATION_KEY, application);
  window.sessionStorage.setItem(SESSION_FILTER_DAY_KEY, String(getDayValueFromLabel(dayLabel)));
}

export function parsePricingSheetApplication(value?: string): PricingSheetApplicationValue {
  const match = PRICING_SHEET_APPLICATIONS.find(app => app.value === value);
  return match?.value ?? DEFAULT_PRICING_SHEET_APPLICATION;
}

/** Formatted string MM-DD ==> Date in current year */
export function dateFromFormattedString(formattedString: string): Date {
  const tempDate = `${moment().format("YYYY")}-${formattedString}`;
  const tempDateArray = tempDate.split("-");

  try {
    return new Date(Number(tempDateArray[0]), Number(tempDateArray[1]) - 1, Number(tempDateArray[2]));
  } catch {
    return new Date();
  }
}

export function diffVariantIds(initialVariantIds: number[], currentVariantIds: number[]) {
  const initialSet = new Set(initialVariantIds);
  const currentSet = new Set(currentVariantIds);

  return {
    addedVariantIds: currentVariantIds.filter(id => !initialSet.has(id)),
    removedVariantIds: initialVariantIds.filter(id => !currentSet.has(id)),
  };
}

export function formatPricingSheetDateRange(startingDate: Date, endingDate: Date) {
  return [formatDate(startingDate), formatDate(endingDate)];
}

/** Normalize DB times like HH:mm:ss to HH:mm for range compares. */
export function normalizePricingSheetTime(time: string): string {
  if (!time) {
    return "";
  }

  const match = time.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) {
    return "";
  }

  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

/** Season (MM-DD) and time (HH:mm) ranges use inclusive string compare, matching pricing lookup. */
export function pricingSheetRangesOverlap(
  startA: string,
  endA: string,
  startB: string,
  endB: string,
): boolean {
  if (!startA || !endA || !startB || !endB) {
    return false;
  }

  return startA <= endB && startB <= endA;
}

export function pricingSheetDateRangesOverlap(
  startA: string,
  endA: string,
  startB: string,
  endB: string,
): boolean {
  return pricingSheetRangesOverlap(startA, endA, startB, endB);
}

export type PricingSheetDateConflictCandidate = {
  id?: number | null;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  application: string;
  courseId: number | null;
  days: string[];
};

export function findConflictingPricingSheets(
  existingSheets: Array<{
    id: number;
    application?: string;
    course_id?: number | null;
    day_of_week?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    title?: string;
  }>,
  candidate: PricingSheetDateConflictCandidate,
  options?: { compareCourse?: boolean },
) {
  const compareCourse = options?.compareCourse === true;
  const candidateDays = new Set(candidate.days.filter(Boolean));

  if (candidateDays.size === 0) {
    return [];
  }

  return existingSheets.filter(existingSheet => {
    if (candidate.id != null && existingSheet.id === candidate.id) {
      return false;
    }

    if (!candidateDays.has(existingSheet.day_of_week ?? "")) {
      return false;
    }

    if (String(existingSheet.application ?? "") !== candidate.application) {
      return false;
    }

    if (compareCourse && (existingSheet.course_id ?? null) !== candidate.courseId) {
      return false;
    }

    const datesOverlap = pricingSheetRangesOverlap(
      candidate.startDate,
      candidate.endDate,
      existingSheet.start_date ?? "",
      existingSheet.end_date ?? "",
    );

    if (!datesOverlap) {
      return false;
    }

    return pricingSheetRangesOverlap(
      normalizePricingSheetTime(candidate.startTime),
      normalizePricingSheetTime(candidate.endTime),
      normalizePricingSheetTime(existingSheet.start_time ?? ""),
      normalizePricingSheetTime(existingSheet.end_time ?? ""),
    );
  });
}

export function isMultiVariantProduct(variant: Partial<IVariant>) {
  return (variant.product?.variant_count ?? 1) > 1;
}

export function groupVariantsByProduct(variants: Array<Partial<IVariant>>): TProductVariantGroup[] {
  const groups: TProductVariantGroup[] = [];
  const groupMap = new Map<number, TProductVariantGroup>();

  variants.forEach(variant => {
    const productId = variant.product_id ?? variant.product?.id;
    if (productId == null) {
      return;
    }

    let group = groupMap.get(productId);
    if (!group) {
      group = {
        productId,
        productTitle: variant.product?.preferred_title || variant.product?.title || "",
        isMultiVariant: isMultiVariantProduct(variant),
        variants: [],
      };
      groupMap.set(productId, group);
      groups.push(group);
    }

    group.variants.push(variant);
  });

  return groups;
}

export function getVariantProductIds(variants: Array<Partial<IVariant>>): number[] {
  const productIds = new Set<number>();

  variants.forEach(variant => {
    const productId = variant.product_id ?? variant.product?.id;
    if (productId != null) {
      productIds.add(productId);
    }
  });

  return Array.from(productIds);
}

export function variantsFromSelectedProducts(products: IProduct[]): Array<Partial<IVariant>> {
  const variants: Array<Partial<IVariant>> = [];

  products.forEach((product: IProduct) => {
    (product.variants ?? []).forEach((variant: IVariant) => {
      variants.push({
        ...variant,
        product: {
          ...(variant.product ?? {}),
          id: product.id,
          title: product.title,
          preferred_title: product.preferred_title,
          type: product.type,
          variant_count: product.variant_count ?? product.variants?.length ?? 1,
        },
      } as Partial<IVariant>);
    });
  });

  return variants;
}

export function mergeVariants(
  existing: Array<Partial<IVariant>>,
  incoming: Array<Partial<IVariant>>,
): Array<Partial<IVariant>> {
  const merged = [...existing];
  const existingIds = new Set(existing.map(variant => variant.id));

  incoming.forEach(variant => {
    if (variant.id != null && !existingIds.has(variant.id)) {
      merged.push(variant);
      existingIds.add(variant.id);
    }
  });

  return merged;
}

export function normalizeLoadedVariants(variants: IVariant[]): Array<Partial<IVariant>> {
  return variants.map(variant => ({
    ...variant,
    product: variant.product
      ? {
          ...variant.product,
          variant_count: variant.product.variant_count ?? 1,
        }
      : variant.product,
  }));
}
